/*
 * <a4-party-pack> — the ScribblePop party-pack band (A4, option 5) on the
 * collection and the four tablet PDPs: sections/a4-party-pack.liquid.
 * Spec: docs/SCRIBBLEPOP_A4_BUILD.md §2 (the sync design) and §5 step 3.
 *
 * ── THE CART IS THE ONLY STATE (§2.1) ────────────────────────────────────────
 * The band is a Liquid render of the cart; nothing here knows a price, a count
 * or a state. Its three jobs:
 *
 *   1. Taps. "+" / "+ Add" -> A4Cart.add(variant), "−" -> A4Cart.change(line
 *      key, that line's quantity - 1). A4Cart (assets/a4-cart.js) writes the
 *      cart, re-renders every cart region from the write's own response (this
 *      band is the A4PartyPack spec in assets/cart-regions.js), holds the one-
 *      write lock (§2.4.5) and never opens the drawer (§2.4.6). The key and the
 *      quantity are read off the tapped button, which the last render wrote:
 *      a line's key changes when its quantity does, so none is ever kept here.
 *
 *   2. Other writers (§2.4.2). A cartUpdate with source 'cart-items' was
 *      rendered from its own response (cart.js steppers, the drawer tiles,
 *      grid cards, A4Cart), so it needs nothing. Any other source (product-
 *      form: the buy box, showcase Buy Now, the bundle card) did not render
 *      this band, so it is fetched once from window.location.pathname: a
 *      template section only renders in its own page's context, and
 *      routes.cart_url would be the wrong one.
 *
 *   3. Back/forward cache (§2.4.7) and tab focus (review R-21). A page
 *      restored from bfcache shows the cart as it was when the shopper left,
 *      and a tab that was in the background may have had its cart changed by
 *      another tab — a "−" here would then carry a dead line key. So on
 *      pageshow with persisted, and whenever the document becomes visible,
 *      every cart region on the page is fetched again in one request
 *      (CartRegions.fetchAndRender), unless an A4 write is in flight — its
 *      response paints everything, and a GET racing it could paint an older
 *      photograph last.
 *
 * ── A STALE REFETCH NEVER PAINTS OVER A NEWER CART ───────────────────────────
 * Every cartUpdate bumps a counter. A refetch that comes back after a newer
 * update is discarded and made again, so the last paint always reflects the
 * last write. 'cart-items' updates only mark an in-flight refetch stale; they
 * never start one.
 *
 * Globals used: subscribe / PUB_SUB_EVENTS (pubsub.js, constants.js), A4Cart
 * and CartRegions (tagged by snippets/cart-drawer.liquid on every page, ahead
 * of content_for_layout, so both exist before this file runs).
 */
if (!customElements.get('a4-party-pack')) {
  customElements.define(
    'a4-party-pack',
    class A4PartyPack extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
        this.onPageShow = this.onPageShow.bind(this);
        this.onVisibilityChange = this.onVisibilityChange.bind(this);
        this.generation = 0;
        this.refreshing = false;
        this.stale = false;
        this.refetchingAll = false;
      }

      connectedCallback() {
        this.addEventListener('click', this.onClick);
        window.addEventListener('pageshow', this.onPageShow);
        document.addEventListener('visibilitychange', this.onVisibilityChange);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        } else {
          // R-25: without this subscription the band goes stale after every buy-box
          // add, with nothing else to show for it.
          console.warn('<a4-party-pack>: pubsub.js is not loaded, so the band cannot hear the buy box and will show a stale count after its adds.');
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        window.removeEventListener('pageshow', this.onPageShow);
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        if (this.unsubscribe) this.unsubscribe();
      }

      get region() {
        return { id: this.id, section: this.dataset.id, selector: '.js-contents' };
      }

      onCartUpdate(event) {
        this.generation += 1;
        if (event && event.source === 'cart-items') {
          if (this.refreshing) this.stale = true;
          return;
        }
        this.refresh();
      }

      refresh() {
        if (this.refreshing) {
          this.stale = true;
          return;
        }
        this.refreshing = true;
        this.stale = false;
        const generation = this.generation;
        const region = this.region;

        window.CartRegions.fetchSections([region])
          .then((sections) => {
            if (generation !== this.generation) {
              this.stale = true;
              return;
            }
            // A null entry (the id is gone from the serving theme) renders "off"
            // through the region's spec — see cart-regions.js, R-06.
            window.CartRegions.renderSections([region], sections);
          })
          .catch((error) => console.error(error))
          .finally(() => {
            this.refreshing = false;
            if (this.stale) this.refresh();
          });
      }

      /* One request for every cart region on the page, drawer and bubble included,
         rendered from this page's own path. The empty-state flags sit outside every
         swapped region, so they are set from the fresh DOM. One at a time: a bfcache
         restore fires pageshow AND visibilitychange, and that is one refetch, not
         two. Skipped while an A4 write is in flight (its response paints everything).
         No cartUpdate is published: the perk meter and the quantity rules would each
         answer with a GET of their own on every tab switch, for a projection that
         self-heals on the next write anyway. */
      refetchAll() {
        if (!window.CartRegions || this.refetchingAll) return;
        if (window.A4Cart && window.A4Cart.busy) return;
        const list = window.CartRegions.sectionsToRender();
        if (!list.length) return;
        this.generation += 1;
        this.refetchingAll = true;

        window.CartRegions.fetchAndRender(list)
          .then(() => window.CartRegions.syncEmptyStateFromDom())
          .catch((error) => console.error(error))
          .finally(() => {
            this.refetchingAll = false;
          });
      }

      onPageShow(event) {
        if (event.persisted) this.refetchAll();
      }

      onVisibilityChange() {
        if (document.visibilityState === 'visible') this.refetchAll();
      }

      onClick(event) {
        const control = event.target.closest('[data-a4-add], [data-a4-change]');
        if (!control || !this.contains(control)) return;
        event.preventDefault();
        if (!window.A4Cart || control.getAttribute('aria-disabled') === 'true' || window.A4Cart.busy) return;

        const adding = control.hasAttribute('data-a4-add');
        const kind = adding ? 'data-a4-add' : 'data-a4-change';
        const name = control.dataset.a4Name || '';
        const tile = control.closest('[data-a4-tile]');
        const tileId = tile ? tile.dataset.a4Tile : '';
        const live = this.querySelector('[data-a4-live]');

        const options = {
          liveRegion: live ? live.id : null,
          // "Lagoon Blue added · 3 of 4 in your cart" (§2.4.6). The status is the
          // server's, read off the band this write just rendered.
          announce: () => {
            const inner = this.querySelector('[data-a4-status]');
            const status = inner ? inner.dataset.a4Status : '';
            const verb = adding ? 'added' : 'removed';
            return status ? `${name} ${verb} · ${status}` : `${name} ${verb}`;
          },
          // The same control in the same tile, so repeated taps keep their place.
          // A tile that just flipped state has only the other kind (the stepper's
          // "+" after "+ Add", "+ Add" after the last "−"); failing that, the heading.
          refocus: () => {
            const scope = tileId ? this.querySelector(`[data-a4-tile="${tileId}"]`) : null;
            const same = scope && scope.querySelector(`[${kind}]`);
            if (same) return same;
            const any = scope && scope.querySelector('[data-a4-add], [data-a4-change]');
            if (any) return any;
            return this.querySelector('.a4-party-pack__title');
          },
        };

        this.showError(null);
        control.classList.add('loading');

        const request = adding
          ? window.A4Cart.add(control.dataset.variantId, options)
          : window.A4Cart.change(control.dataset.lineKey, Number(control.dataset.lineQty) - 1, options);

        request
          .then((result) => {
            if (!result.ok && !result.skipped) this.showError(result.message);
          })
          .finally(() => {
            control.classList.remove('loading');
          });
      }

      showError(message) {
        const slot = this.querySelector('[data-a4-error]');
        if (!slot) return;
        slot.toggleAttribute('hidden', !message);
        slot.textContent = message || '';
      }
    }
  );
}
